import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../redux/store";
import {
  listenToInbox,
  unsubscribeIMessage,
  IThread,
} from "../../redux/iMessageSlice";
import { Search, X, MessageCircle } from "lucide-react";

// ── Design tokens ─────────────────────────────────────────
const T = {
  gold:       "#C8860A",
  goldLight:  "#E8A020",
  goldPale:   "#FDF6E3",
  goldBorder: "#EAD08A",
  black:      "#0A0A0A",
  offBlack:   "#1A1A1A",
  charcoal:   "#2C2C2C",
  white:      "#FFFFFF",
  offWhite:   "#FAFAF8",
  muted:      "#8A8070",
  mutedLight: "#B8AFA0",
  border:     "#EDE8E0",
  surface:    "#F8F5F0",
  unreadBg:   "#FFF8E8",
};

// ── Helpers ───────────────────────────────────────────────
const toMs = (ts: any): number | null => {
  if (!ts) return null;
  if (ts?.toMillis) return ts.toMillis();
  if (ts?.seconds)  return ts.seconds * 1000;
  if (typeof ts === "number") return ts;
  return null;
};

const formatTime = (ts: any): string => {
  const ms = toMs(ts);
  if (!ms) return "";
  const d       = new Date(ms);
  const now     = new Date();
  const today   = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.floor((today.getTime() - d.getTime()) / 86400000);
  if (diffDays === 0) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7)   return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// ── Avatar ────────────────────────────────────────────────
const Avatar: React.FC<{
  src?: string; name: string; size?: number; unread?: boolean; online?: boolean;
}> = ({ src, name, size = 52, unread = false, online = false }) => {
  const initials = getInitials(name);
  const fontSize = Math.round(size * 0.34);
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      {unread && (
        <div style={{
          position: "absolute", inset: -2.5, borderRadius: "50%",
          background: `conic-gradient(${T.gold}, ${T.goldLight}, ${T.gold})`, zIndex: 0,
        }} />
      )}
      <div style={{
        position: "relative", width: size, height: size, borderRadius: "50%",
        overflow: "hidden",
        background: unread ? T.offBlack : `linear-gradient(135deg, ${T.charcoal}, ${T.offBlack})`,
        display: "flex", alignItems: "center", justifyContent: "center",
        border: `2px solid ${T.white}`, zIndex: 1,
      }}>
        {src
          ? <img src={src} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <span style={{
              color: unread ? T.gold : T.mutedLight, fontSize, fontWeight: 700,
              fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
              letterSpacing: "0.04em", lineHeight: 1,
            }}>{initials}</span>
        }
      </div>
      {online && (
        <div style={{
          position: "absolute", bottom: 2, right: 2, width: 10, height: 10,
          borderRadius: "50%", background: "#22C55E", border: `2px solid ${T.white}`, zIndex: 2,
        }} />
      )}
    </div>
  );
};

// ── Thread row ────────────────────────────────────────────
const ThreadRow: React.FC<{
  thread: IThread; otherUserName: string; otherUserPhoto?: string;
  currentUserId: string; isLast: boolean; onClick: () => void; animDelay: number;
}> = ({ thread, otherUserName, otherUserPhoto, currentUserId, isLast, onClick, animDelay }) => {
  const [hovered, setHovered] = useState(false);
  const unreadCount = thread.unread?.[currentUserId] ?? 0;
  const isUnread    = unreadCount > 0;
  const isMe        = thread.lastMessage?.senderId === currentUserId;
  const preview     = thread.lastMessage?.text ?? "";
  const time        = formatTime(thread.updatedAt);

  return (
    <div style={{ animationDelay: `${animDelay}ms` }} className="thread-slide-in">
      <div
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: "flex", alignItems: "center", gap: 14,
          padding: "14px 20px", cursor: "pointer",
          background: hovered
            ? isUnread ? T.unreadBg : T.surface
            : isUnread ? T.goldPale : T.white,
          transition: "background 0.18s ease", position: "relative",
        }}
      >
        {isUnread && (
          <div style={{
            position: "absolute", left: 0, top: "20%", bottom: "20%",
            width: 3, borderRadius: "0 3px 3px 0",
            background: `linear-gradient(to bottom, ${T.gold}, ${T.goldLight})`,
          }} />
        )}

        <Avatar src={otherUserPhoto} name={otherUserName} size={52} unread={isUnread} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: "flex", alignItems: "baseline",
            justifyContent: "space-between", gap: 8, marginBottom: 4,
          }}>
            <span style={{
              fontSize: 15, fontWeight: isUnread ? 700 : 500, color: T.black,
              fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: "-0.01em",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1,
            }}>
              {otherUserName}
            </span>
            <span style={{
              fontSize: 11, color: isUnread ? T.gold : T.mutedLight,
              fontWeight: isUnread ? 600 : 400, flexShrink: 0,
              letterSpacing: "0.02em", fontFamily: "'DM Mono', monospace",
            }}>
              {time}
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{
              fontSize: 13, color: isUnread ? T.charcoal : T.muted,
              fontWeight: isUnread ? 500 : 400, whiteSpace: "nowrap",
              overflow: "hidden", textOverflow: "ellipsis", flex: 1,
              lineHeight: 1.4, fontStyle: !preview ? "italic" : "normal",
            }}>
              {isMe && preview
                ? <span><span style={{ color: T.gold, fontWeight: 600 }}>You: </span>{preview}</span>
                : preview || "No messages yet"
              }
            </span>
            {isUnread && (
              <div style={{
                minWidth: 20, height: 20, borderRadius: 10,
                background: `linear-gradient(135deg, ${T.gold}, ${T.goldLight})`,
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "0 6px", flexShrink: 0, boxShadow: `0 2px 8px ${T.gold}55`,
              }}>
                <span style={{
                  color: T.white, fontSize: 10, fontWeight: 800,
                  lineHeight: 1, fontFamily: "'DM Mono', monospace",
                }}>
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {!isLast && (
        <div style={{
          height: 1,
          background: `linear-gradient(to right, transparent, ${T.border} 80px, ${T.border} calc(100% - 20px), transparent)`,
          marginLeft: 86,
        }} />
      )}
    </div>
  );
};

// ── Empty state ───────────────────────────────────────────
const EmptyState: React.FC<{ searching: boolean; query: string }> = ({ searching, query }) => (
  <div style={{
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: "60px 32px", gap: 16, textAlign: "center",
  }}>
    <div style={{
      width: 72, height: 72, borderRadius: "50%", background: T.surface,
      border: `1.5px solid ${T.border}`, display: "flex",
      alignItems: "center", justifyContent: "center", marginBottom: 4,
    }}>
      {searching
        ? <Search size={28} color={T.mutedLight} strokeWidth={1.5} />
        : <MessageCircle size={28} color={T.mutedLight} strokeWidth={1.5} />
      }
    </div>
    <div style={{
      fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 22,
      fontWeight: 700, color: T.black, letterSpacing: "-0.02em",
    }}>
      {searching ? `No results for "${query}"` : "No conversations yet"}
    </div>
    <div style={{ fontSize: 13, color: T.muted, maxWidth: 220, lineHeight: 1.65, fontWeight: 400 }}>
      {searching
        ? "Try searching by name or message content"
        : "Your conversations with hosts and guests will appear here"
      }
    </div>
  </div>
);

// ── Main component ────────────────────────────────────────
const IMessagePreview: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { user, allUsers }              = useSelector((s: RootState) => s.auth);
  const { threads, threadsLoading }     = useSelector((s: RootState) => s.imessage);

  const [search,     setSearch]     = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const inputRef     = useRef<HTMLInputElement>(null);
  const currentUserId = user?.userId ?? "";

  // ── Debug logs ────────────────────────────────────────
  useEffect(() => {
    console.group("[IMessagePreview] 🔍 State snapshot");
    console.log("currentUserId :", currentUserId || "⚠️ EMPTY — auth not loaded yet");
    console.log("allUsers count:", allUsers?.length ?? 0, allUsers?.length === 0 ? "⚠️ allUsers is empty — fetchAllUsers may not have been called" : "");
    console.log("threads count :", threads.length, threads.length === 0 ? "⚠️ No threads — inbox listener may not have fired yet" : "");
    console.log("threadsLoading:", threadsLoading);
    console.groupEnd();
  }, [currentUserId, allUsers, threads, threadsLoading]);

  useEffect(() => {
    if (!threads.length) return;
    console.group("[IMessagePreview] 🧵 Raw threads");
    threads.forEach((t, i) => {
      const matched = allUsers?.find(u => u.userId === t.otherUserId);
      console.log(
        `Thread ${i}:`,
        t.threadId,
        "| otherUserId:", t.otherUserId || "⚠️ MISSING",
        "| matched user:", matched
          ? `${matched.firstName} ${matched.lastName}`
          : "⚠️ NOT FOUND IN allUsers",
        "| lastMessage:", t.lastMessage?.text ?? "(none)",
        "| unread:", t.unread,
      );
    });
    console.groupEnd();
  }, [threads, allUsers]);

  // ── Inbox listener ────────────────────────────────────
  useEffect(() => {
    if (!currentUserId) {
      console.warn("[IMessagePreview] ⛔ listenToInbox skipped — currentUserId is empty");
      return;
    }
    console.log("[IMessagePreview] ✅ Starting inbox listener for:", currentUserId);
    dispatch(listenToInbox({ currentUserId }));
    return () => unsubscribeIMessage(`inbox_${currentUserId}`);
  }, [dispatch, currentUserId]);

  useEffect(() => {
    if (searchOpen) setTimeout(() => inputRef.current?.focus(), 80);
  }, [searchOpen]);

  // ── Enrich threads with user data ─────────────────────
  type EnrichedThread = { thread: IThread; name: string; photo: string | undefined };

  const enriched: EnrichedThread[] = threads
    .map((thread): EnrichedThread => {
      const other = allUsers?.find(u => u.userId === thread.otherUserId);
      const name  = other
        ? `${other.firstName ?? ""} ${other.lastName ?? ""}`.trim() || "User"
        : "User";
      return { thread, name, photo: other?.profilePicture };
    })
    .filter(({ thread, name }: EnrichedThread) =>
      !search.trim() ||
      name.toLowerCase().includes(search.toLowerCase()) ||
      (thread.lastMessage?.text ?? "").toLowerCase().includes(search.toLowerCase())
    );

  const totalUnread = threads.reduce((sum, t) => sum + (t.unread?.[currentUserId] ?? 0), 0);

  return (
    <div style={S.root}>
      <style>{CSS}</style>

      {/* ── Header ── */}
      <div style={S.header}>
        <div style={S.headerInner}>
          <div style={S.headerLeft}>
            <div style={S.titleWrap}>
              <h1 style={S.title}>Messages</h1>
              {totalUnread > 0 && (
                <div style={S.totalBadge}>
                  <span style={S.totalBadgeText}>{totalUnread > 99 ? "99+" : totalUnread}</span>
                </div>
              )}
            </div>
            {!searchOpen && (
              <p style={S.subtitle}>
                {threads.length === 0
                  ? "Start a conversation"
                  : `${threads.length} conversation${threads.length !== 1 ? "s" : ""}`
                }
              </p>
            )}
          </div>

          <button
            style={{ ...S.searchBtn, background: searchOpen ? T.surface : "transparent" }}
            onClick={() => { setSearchOpen(v => !v); if (searchOpen) setSearch(""); }}
            aria-label={searchOpen ? "Close search" : "Search"}
          >
            {searchOpen ? <X size={17} color={T.charcoal} /> : <Search size={17} color={T.charcoal} />}
          </button>
        </div>

        {searchOpen && (
          <div style={S.searchBar} className="search-slide-down">
            <Search size={14} color={T.mutedLight} style={{ flexShrink: 0 }} />
            <input
              ref={inputRef}
              style={S.searchInput}
              placeholder="Search conversations…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }} onClick={() => setSearch("")}>
                <X size={13} color={T.mutedLight} />
              </button>
            )}
          </div>
        )}

        <div style={S.headerDivider} />
      </div>

      {/* ── List ── */}
      <div style={S.list}>
        {/* Loading shimmer */}
        {threadsLoading && threads.length === 0 && (
          <div style={{ padding: "12px 20px" }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 0", opacity: 1 - i * 0.25 }}>
                <div className="shimmer" style={{ width: 52, height: 52, borderRadius: "50%" }} />
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                  <div className="shimmer" style={{ height: 14, width: "45%", borderRadius: 6 }} />
                  <div className="shimmer" style={{ height: 12, width: "70%", borderRadius: 6 }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!threadsLoading && enriched.length === 0 && (
          <EmptyState searching={!!search.trim()} query={search} />
        )}

        {/* Thread rows */}
        {enriched.map(({ thread, name, photo }, i) => (
          <ThreadRow
            key={thread.threadId}
            thread={thread}
            otherUserName={name}
            otherUserPhoto={photo}
            currentUserId={currentUserId}
            isLast={i === enriched.length - 1}
            animDelay={i * 40}
            onClick={() => navigate(`/imessages/${thread.threadId}`)}
          />
        ))}

        <div style={{ height: "max(40px, env(safe-area-inset-bottom))" }} />
      </div>
    </div>
  );
};

export default IMessagePreview;

// ── Global CSS ────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  @keyframes threadSlideIn {
    from { opacity: 0; transform: translateX(-12px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes searchSlideDown {
    from { opacity: 0; transform: translateY(-8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }

  .thread-slide-in   { animation: threadSlideIn 0.3s ease both; }
  .search-slide-down { animation: searchSlideDown 0.2s ease both; }
  .shimmer {
    background: linear-gradient(90deg, #F0EDE8 25%, #E8E4DC 50%, #F0EDE8 75%);
    background-size: 800px 100%;
    animation: shimmer 1.4s infinite linear;
  }

  input:focus { outline: none; }
  ::-webkit-scrollbar { width: 0; }

  @media (max-width: 480px) {
    .msg-root { border-left: none !important; border-right: none !important; }
  }
`;

// ── Styles ────────────────────────────────────────────────
const S: Record<string, React.CSSProperties> = {
  root: {
    display: "flex", flexDirection: "column", height: "100dvh",
    maxWidth: 520, margin: "0 auto", backgroundColor: T.white,
    fontFamily: "'DM Sans', sans-serif",
    borderLeft: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}`,
    overflow: "hidden", position: "relative",
  },
  header: { flexShrink: 0, backgroundColor: T.white, paddingTop: "max(16px, env(safe-area-inset-top))" },
  headerInner: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "6px 20px 12px" },
  headerLeft: { display: "flex", flexDirection: "column", gap: 4 },
  titleWrap: { display: "flex", alignItems: "center", gap: 10 },
  title: {
    fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 32,
    fontWeight: 700, color: T.black, letterSpacing: "-0.04em", lineHeight: 1,
  },
  totalBadge: {
    display: "flex", alignItems: "center", gap: 4,
    background: `linear-gradient(135deg, ${T.gold}, ${T.goldLight})`,
    borderRadius: 20, padding: "4px 8px", boxShadow: `0 2px 10px ${T.gold}44`,
  },
  totalBadgeText: { color: T.white, fontSize: 11, fontWeight: 700, lineHeight: 1, fontFamily: "'DM Mono', monospace" },
  subtitle: { fontSize: 12, color: T.mutedLight, fontWeight: 400, letterSpacing: "0.06em", textTransform: "uppercase" as const },
  searchBtn: {
    width: 36, height: 36, borderRadius: "50%", border: `1px solid ${T.border}`,
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
    transition: "background 0.15s", flexShrink: 0, marginTop: 2,
  },
  searchBar: {
    display: "flex", alignItems: "center", gap: 10,
    margin: "0 20px 12px", padding: "10px 14px",
    background: T.surface, borderRadius: 12, border: `1px solid ${T.border}`,
  },
  searchInput: { flex: 1, border: "none", background: "transparent", fontSize: 14, color: T.black, fontFamily: "'DM Sans', sans-serif", fontWeight: 400 },
  headerDivider: { height: 1, background: `linear-gradient(to right, transparent, ${T.goldBorder}88 20%, ${T.goldBorder}88 80%, transparent)` },
  list: { flex: 1, overflowY: "auto" as const, backgroundColor: T.white },
};